import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import {
  Headline,
  Paragraph,
  Section,
  ContentBlock,
  Button,
} from "../components/ui";
import HeroSection from "../components/HeroSection";
import { ContactForm } from "../components/ContactForm";

const ContactCard = ({ name, title, email, phone, image }) => {
  return (
    <div
      className="rounded-sm overflow-hidden shadow-lg flex flex-col md:flex-row"
      style={{ backgroundColor: "var(--color-secondary)" }}
    >
      {/* Image - left on desktop, top on mobile */}
      <div className="md:w-1/3 flex-shrink-0">
        <img
          src={image}
          alt={name}
          className="w-full h-[420px] sm:h-64 md:h-full object-cover"
        />
      </div>

      {/* Content - right on desktop, bottom on mobile */}
      <div className="flex-1 p-6 flex flex-col justify-center">
        <Headline
          variant="splitscreen-heading"
          color="primary"
          className="text-left"
        >
          {name}
        </Headline>
        <Paragraph
          variant="medium"
          color="primary"
          className="mt-[-6px] opacity-75"
        >
          {title}
        </Paragraph>
        <div className="space-1-2">
          <div>
            <a
              href={`mailto:${email}`}
              className="hover:opacity-75 transition-opacity"
              style={{ color: "var(--color-highlight)" }}
            >
              <Paragraph variant="medium" color="primary">
                {email}
              </Paragraph>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`tel:${phone.replace(/\s/g, "")}`}
              className="hover:opacity-75 transition-opacity"
              style={{ color: "var(--color-highlight)" }}
            >
              <Paragraph variant="medium" color="primary">
                {phone}
              </Paragraph>
            </a>
            <Button
              variant="primary"
              size="small"
              className="bg-green-500 hover:bg-green-600 text-white font-medium px-3 py-2 rounded-full text-xs mt-[-20px]"
              onClick={() =>
                window.open(
                  `https://wa.me/${phone
                    .replace(/\s/g, "")
                    .replace(
                      /\+/g,
                      ""
                    )}?text=Hallo, ich möchte Sie kontaktieren.`,
                  "_blank"
                )
              }
            >
              WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainContactSection = () => {
  return (
    <Section variant="primary" spacing="none" margin={false}>
      <div className="max-w-7xl pb-0 sm:pt-0 mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px]">
          {/* Image - left on desktop, top on mobile */}
          <div className="order-1 lg:order-1">
            <img
              src="https://res.cloudinary.com/donwh3eei/image/upload/v1756149674/gallery/frank_kayser_shooting/Screenshot_2025-08-25_at_9.21.07_PM_vawsu1.png"
              alt="WTM Sportwagen Werkstatt"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Address and contact info - right on desktop, bottom on mobile */}
          <div
            className="order-2 lg:order-2"
            style={{
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "left",
            }}
          >
            <div>
              <div style={{ marginBottom: "20px", padding: "0 20px 0 20px" }}>
                <Headline
                  variant="splitscreen-heading"
                  color="primary"
                  className="mb-2 text-left"
                >
                  WTM Sportwagen
                </Headline>
                <Paragraph variant="medium" color="primary" noSpacing>
                  Grünberger Straße 140
                </Paragraph>
                <Paragraph variant="medium" color="primary" noSpacing>
                  35394 Gießen
                </Paragraph>
                <Paragraph variant="medium" color="primary" noSpacing>
                  Deutschland
                </Paragraph>
              </div>

              <div style={{ padding: "0 20px 20px 20px" }}>
                <Paragraph variant="medium" color="primary" noSpacing>
                  Fahrzeugbesichtigung nur nach Terminvereinbarung
                </Paragraph>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Contact = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL and scroll to it
    if (location.hash === "#contact-form") {
      setTimeout(() => {
        const element = document.getElementById("contact-form");
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100); // Small delay to ensure page is rendered
    }
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>Kontakt | WTM Sportwagen - Freie Porsche Werkstatt Gießen</title>
        <meta
          name="description"
          content="Kontaktieren Sie WTM Sportwagen in Gießen für Porsche Motorenbau, Restauration, Service & Inspektion. Persönliche Beratung, deutschlandweiter Hol-/Bringservice. Jetzt Termin vereinbaren."
        />
        <meta
          name="keywords"
          content="WTM Sportwagen Kontakt, Porsche Werkstatt Gießen, Porsche Service Termin, Motorenbau Beratung, Restauration Anfrage, Porsche Inspektion Termin"
        />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="Kontakt | WTM Sportwagen - Ihr Porsche Spezialist"
        />
        <meta
          property="og:description"
          content="Kontaktieren Sie WTM Sportwagen für professionellen Porsche Service, Motorenbau und Restauration in Gießen."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wtm-sportwagen.com/kontakt" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Kontakt | WTM Sportwagen" />
        <meta
          name="twitter:description"
          content="Kontaktieren Sie WTM Sportwagen für professionellen Porsche Service in Gießen."
        />

        {/* Local Business Schema */}
        <meta name="geo.region" content="DE-HE" />
        <meta name="geo.placename" content="Gießen" />
        <meta name="geo.position" content="50.5848;8.6782" />
        <meta name="ICBM" content="50.5848, 8.6782" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://wtm-sportwagen.com/kontakt" />
      </Helmet>

      <div
        style={{
          minHeight: "100vh",
        }}
      >
        {/* Full-screen hero image */}
        <HeroSection
          backgroundImage="https://res.cloudinary.com/donwh3eei/image/upload/v1756130378/gallery/frank_kayser_shooting/Screenshot_2025-08-25_at_3.58.28_PM_bogpvi.png"
          headline="HERZLICH WILLKOMMEN"
          description="Ihre Leidenschaft ist Porsche – unsere auch. Ob Motorenbau, Restauration oder Service: Wir sind persönlich für Sie da."
          hasVignette={true}
          overlayOpacity={0.1}
          renderDescription={true}
        />

        {/* Contact Cards Section */}
        <Section
          variant="primary"
          spacing="comfortable_top_comfortable_bottom"
          margin={false}
        >
          <ContentBlock className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Two contact cards - side by side on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ContactCard
                name="Thomas Thiesen"
                title="Werkstattleiter"
                email="werkstatt@wtm-sportwagen.com"
                phone="+49 157 568 50 714"
                image="https://res.cloudinary.com/donwh3eei/image/upload/v1756137421/gallery/frank_kayser_shooting/thomas_mbovj4.jpg"
              />
              <ContactCard
                name="Andreas Wutschke"
                title="Geschäftsführer"
                email="werkstatt@wtm-sportwagen.com"
                phone="+49 179 3434 888"
                image="https://res.cloudinary.com/donwh3eei/image/upload/v1756137421/gallery/frank_kayser_shooting/Screenshot_2025-08-25_at_5.48.37_PM_jcfqpu.png"
              />
            </div>
          </ContentBlock>
        </Section>

        {/* Main Contact Section */}
        <MainContactSection />

        {/* Contact Form Section */}
        <ContactForm
          headline="Weitere Anfragen?"
          intro="Nutzen Sie unser Kontaktformular für detaillierte Anfragen."
          formHeadline="Wir freuen uns von Ihnen zu hören"
          // formIntro="Füllen Sie alle Felder aus und wir melden uns umgehend bei Ihnen."
        />
      </div>
    </>
  );
};

export default Contact;
